import { helper } from '@ember/component/helper'
import type { TaskInstance } from 'ember-concurrency'
import type ErrorHandler from 'teacher-ui-client/utils/error-handler'

type Nullish = undefined | null
type PositionalArgs = [TaskInstance<unknown> | Nullish, ErrorHandler | Nullish]

function hasErrors(maybeHasErrors: unknown): maybeHasErrors is { errors: Record<string, unknown>[] } {
  return Boolean(
    maybeHasErrors &&
    typeof maybeHasErrors === 'object' &&
    'errors' in maybeHasErrors &&
    Array.isArray(maybeHasErrors.errors) &&
    maybeHasErrors.errors.every((error) => typeof error === 'object'),
  )
}

function hasError(maybeHasError: unknown): maybeHasError is { errors: Record<string, unknown> } {
  return Boolean(
    maybeHasError &&
    typeof maybeHasError === 'object' &&
    'errors' in maybeHasError &&
    typeof maybeHasError.errors === 'object',
  )
}

enum SubmitState {
  Default = 'default',
  Load = 'load',
  Done = 'done',
  Error = 'error',
}

/**
 * Uses task instance to return 1 of 4 button states
 * Whenever task.{isRunning,isError,isSuccessful} are changing the helper will return, `default, load, done or error`
 *
 * Computed prop which get executed when the task props isRunning,isError,isSuccessful
 * are executed. If we pass in an error handler and the errorhandler will deal with all errors
 * then we change the return value to default which omits the display of an error.
 */
export function submitButtonState([task, errorHandler]: PositionalArgs): SubmitState {
  if (!task) return SubmitState.Default
  if (task.isRunning) return SubmitState.Load
  if (task.isSuccessful) return SubmitState.Done

  if (task.isError && errorHandler) {
    const errors = extractErrors(task.error)
    const allErrorsWillBeHandled = errorHandler.testIfAllErrorsWillBeHandled(errors)
    if (allErrorsWillBeHandled) return SubmitState.Default
    return SubmitState.Error
  } else if (task.isError) {
    return SubmitState.Error
  }

  return SubmitState.Default
}

function extractErrors(error: unknown): Record<string, unknown>[] {
  if (hasErrors(error)) return error.errors
  if (hasError(error)) return [error.errors]
  return []
}

export default helper(submitButtonState)
